import type { EffectiveSessionPolicy } from '@ctcells/api'
import { t, type MessageKey } from '@ctcells/i18n'

import { SettingsRow } from '../../layout/SettingsList.tsx'
import type { ActionGate } from '../access.ts'
import { Card } from '../resourceParts.tsx'

import { PolicyNumberInput } from './PolicyNumberInput.tsx'
import { SESSION_FIELDS, ttlText, type SessionField } from './policyModel.ts'

import './generalSettings.css'
import './policies.css'

/**
 * 계정 상세 「세션 만료」 카드 — 그 계정에 실제로 걸리는 세 값과 출처(`IFC-RO-89`), 그리고
 * **그 계정 단 값 넣기 · 지우기**(`PUT /settings/session-policy` scope `user:<user_id>` · `IFC-RO-88` · `07` §4.7 🔵 ⓑ).
 *
 * 🔴 **화면은 판정하지 않는다.** 값 · 출처 · 「무인 사이클보다 짧다」 경고는 전부 서버 응답(`EffectiveSessionPolicy`)
 *    그대로다. 무인 사이클 길이와 비교하거나 출처를 다시 짓지 않는다 — 조합이 틀린 계정은 서버가 이미 기본값으로 바꿔 준다.
 * 🔴 **카드가 서는 것은 컨테이너가 상태를 넘길 때뿐이다**(`UserDetailScreen` `sessionPolicy`). demo 경로는 안 넘긴다 —
 *    목 값으로 「이 계정은 24시간」을 지어내지 않는다.
 * ⚠ 「못 봤다」를 「값 없음」으로 접지 않는다 — 실패면 칸 대신 사유 한 줄이 선다.
 *
 * ## 계정 단 칸
 *
 * 칸에 보이는 것은 **이 계정 단에 적힌 값**이다. 출처가 `user` 인 칸만 그 값과 같고, 나머지는 빈 칸(윗단을 따른다).
 * 고치고 Enter · 칸을 떠나면 바로 나간다(「저장」 없음 · `PolicyNumberInput`). 빈 칸 = 지움.
 * 🔴 정책은 **열린 세션에 소급하지 않는다**(서버 `UAM-SES-06`) — 바꾼 값은 그 계정의 다음 로그인부터다.
 */
export type UserSessionPolicyView =
  | { status: 'loading' }
  | { status: 'error'; text: string }
  | { status: 'ready'; policy: EffectiveSessionPolicy }

/** 계정 단 쓰기. 안 넘기면 칸 없이 보기만 한다 */
export interface UserSessionPolicyEdit {
  gate: ActionGate
  /** 마지막 쓰기의 거절 사유 */
  error?: string | undefined
  /** 거절 횟수 — 바뀌면 칸 글자가 서버 값으로 되돌아간다 */
  failures: number
  onCommit: (field: SessionField, value: number | null) => void
}

type Source = EffectiveSessionPolicy['access_ttl_sec']['source']

const SOURCE_LABEL: Record<Source, MessageKey> = {
  user: 'userSessionPolicy.source.user',
  role: 'userSessionPolicy.source.role',
  default: 'userSessionPolicy.source.default',
  base: 'userSessionPolicy.source.base',
}

const FIELD_LABEL: Record<SessionField, MessageKey> = {
  access_ttl_sec: 'userSessionPolicy.access',
  idle_ttl_sec: 'userSessionPolicy.idle',
  absolute_ttl_sec: 'userSessionPolicy.absolute',
}

/** 출처 표기. 서버가 enum 밖 낱말을 주면 그 원문을 그대로 보인다(지어 접지 않는다) */
function sourceText(source: string, role: string): string {
  const key = (SOURCE_LABEL as Record<string, MessageKey | undefined>)[source]
  return key === undefined ? source : t(key, { role })
}

export function UserSessionPolicyCard({
  view,
  edit,
}: {
  view: UserSessionPolicyView
  edit?: UserSessionPolicyEdit | undefined
}) {
  return (
    <Card title={t('userSessionPolicy.title')}>
      {view.status === 'loading' ? (
        <p className="muted">{t('state.loading')}</p>
      ) : view.status === 'error' ? (
        <p className="muted" role="status">
          {view.text}
        </p>
      ) : (
        <>
          {SESSION_FIELDS.map((field) => {
            const v = view.policy[field]
            return (
              <SettingsRow
                key={field}
                label={t(FIELD_LABEL[field])}
                value={
                  <span data-policy-field={field}>
                    {ttlText(v.value)}{' '}
                    <span className="muted">· {sourceText(v.source, view.policy.role)}</span>
                  </span>
                }
                {...(edit === undefined
                  ? {}
                  : {
                      action: (
                        <PolicyNumberInput
                          failedAt={edit.failures}
                          gate={edit.gate}
                          label={t('userSessionPolicy.userTier', { label: t(FIELD_LABEL[field]) })}
                          onCommit={(next) => edit.onCommit(field, next)}
                          placeholder={t('policy.placeholder.inherit')}
                          scale={60}
                          unit={t('policy.unit.minutes')}
                          value={v.source === 'user' ? v.value : null}
                        />
                      ),
                    })}
              />
            )
          })}
          {view.policy.below_unattended_cycle ? (
            <p className="vissue" role="alert">
              {t('userSessionPolicy.belowCycle')}
            </p>
          ) : null}
          {edit?.error === undefined ? null : (
            <p className="vissue" role="alert">
              {edit.error}
            </p>
          )}
        </>
      )}
    </Card>
  )
}
